import { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Checkbox, Button, Divider, Spin, Tag, message, Empty } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
    getCategoryPermissions,
    addCategoryPermission,
    removeCategoryPermission,
    assignBoardRoles,
} from '../../api/adminAPI';
import { newGetCategory } from '../../api/categoryAPI';
import type { PermissionBoard, BoardCategory } from './hooks/useUserBoardPermissions';
import type { UserData } from './config/columns/userColumns';

interface UserBoardPermissionModalProps {
    open: boolean;
    user: UserData | null;
    permBoards: PermissionBoard[];
    categoriesByBoard: Record<number, BoardCategory[]>;
    /** 처음 열 때 미리 선택해 둘 게시판 (선택) */
    initialBoardId?: number | null;
    onClose: () => void;
    onSaved: () => void;
}

/** 카테고리 하위(자손) id를 모두 수집 — 상위 카테고리 권한 변경 시 자식까지 함께 반영 */
const getSubCategoryIds = async (categoryId: number): Promise<number[]> => {
    try {
        const subCategories = await newGetCategory(categoryId);
        if (!subCategories || subCategories.length === 0) return [];

        let ids: number[] = [];
        for (const category of subCategories) {
            const subId = category.id ?? category.categoryId ?? category.category_id;
            ids.push(subId);
            const childIds = await getSubCategoryIds(subId);
            ids = [...ids, ...childIds];
        }
        return ids;
    } catch (error) {
        console.error('하위 카테고리 조회 실패:', error);
        return [];
    }
};

/**
 * 유저 한 명 기준으로, 여러 게시판의 카테고리 권한을 한 곳에서 부여/회수하는 모달.
 * 게시판을 바꿔가며 편집한 내용을 모아 두었다가 "저장" 시 변경분만 반영한다.
 */
const UserBoardPermissionModal = ({
    open,
    user,
    permBoards,
    categoriesByBoard,
    initialBoardId,
    onClose,
    onSaved,
}: UserBoardPermissionModalProps) => {
    const [selectedBoardId, setSelectedBoardId] = useState<number | null>(null);
    // 게시판별 서버 기준값 / 편집값 (checked 카테고리 id 목록)
    const [initialByBoard, setInitialByBoard] = useState<Record<number, number[]>>({});
    const [editedByBoard, setEditedByBoard] = useState<Record<number, number[]>>({});
    const [loadingBoardId, setLoadingBoardId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const categories = selectedBoardId != null ? categoriesByBoard[selectedBoardId] ?? [] : [];
    const checkedList = selectedBoardId != null ? editedByBoard[selectedBoardId] ?? [] : [];

    // 변경된 게시판 수 (버튼 라벨/활성화용)
    const changedBoardCount = useMemo(() => {
        return Object.keys(editedByBoard).filter((boardIdStr) => {
            const boardId = Number(boardIdStr);
            const initial = initialByBoard[boardId] ?? [];
            const edited = editedByBoard[boardId] ?? [];
            if (initial.length !== edited.length) return true;
            const initialSet = new Set(initial);
            return edited.some((id) => !initialSet.has(id));
        }).length;
    }, [editedByBoard, initialByBoard]);

    // 모달이 열릴 때 초기화 + 기본 게시판 선택
    useEffect(() => {
        if (!open) return;
        setInitialByBoard({});
        setEditedByBoard({});
        setSelectedBoardId(null);
        const firstBoard =
            initialBoardId ?? (permBoards.length > 0 ? permBoards[0].boardId : null);
        if (firstBoard != null) {
            handleSelectBoard(firstBoard);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 게시판 선택 시, 아직 안 불러온 게시판이면 현재 권한을 조회해 기준값으로 저장
    const handleSelectBoard = async (boardId: number) => {
        setSelectedBoardId(boardId);
        if (!user) return;
        if (editedByBoard[boardId] !== undefined) return; // 이미 로드/편집됨

        const board = permBoards.find((b) => b.boardId === boardId);
        if (!board) return;

        setLoadingBoardId(boardId);
        try {
            const res = await getCategoryPermissions(user.userId, board.rootCategoryId);
            const data = res?.data || [];
            const checkedIds = data
                .filter((item: any) => item.hasPermission === true)
                .map((item: any) => item.categoryId);
            setInitialByBoard((prev) => ({ ...prev, [boardId]: checkedIds }));
            setEditedByBoard((prev) => ({ ...prev, [boardId]: checkedIds }));
        } catch (error) {
            message.error('기존 카테고리 권한을 불러오지 못했습니다.');
            setInitialByBoard((prev) => ({ ...prev, [boardId]: [] }));
            setEditedByBoard((prev) => ({ ...prev, [boardId]: [] }));
        } finally {
            setLoadingBoardId(null);
        }
    };

    const handleChange = (values: number[]) => {
        if (selectedBoardId == null) return;
        setEditedByBoard((prev) => ({ ...prev, [selectedBoardId]: values }));
    };

    const handleCheckAll = (e: CheckboxChangeEvent) => {
        if (selectedBoardId == null) return;
        setEditedByBoard((prev) => ({
            ...prev,
            [selectedBoardId]: e.target.checked ? categories.map((c) => c.id) : [],
        }));
    };

    const handleSave = async () => {
        if (!user) return;

        // 변경분 계산
        const updates: Array<{ boardId: number; added: number[]; removed: number[] }> = [];
        Object.entries(editedByBoard).forEach(([boardIdStr, edited]) => {
            const boardId = Number(boardIdStr);
            const initial = initialByBoard[boardId] ?? [];
            const added = edited.filter((id) => !initial.includes(id));
            const removed = initial.filter((id) => !edited.includes(id));
            if (added.length > 0 || removed.length > 0) {
                updates.push({ boardId, added, removed });
            }
        });

        if (updates.length === 0) {
            message.info('변경된 권한이 없습니다.');
            return;
        }

        setIsSaving(true);
        try {
            const promises: Promise<any>[] = [];

            for (const update of updates) {
                // 게시판 목록(/boards-with-root-category 등)은 카테고리 권한이 아니라
                // 게시판 역할(TOTAL) 기준으로 필터링된다. 따라서 게시판 역할을 카테고리 권한과
                // 연동해 관리한다:
                //  - 카테고리를 하나라도 보유하게 되면(신규 부여) TOTAL 역할 부여
                //  - 카테고리를 모두 회수하면 TOTAL 역할도 회수(빈 배열 = 역할 해제, ConferenceRoleModal과 동일)
                const finalIds = editedByBoard[update.boardId] ?? [];
                if (finalIds.length === 0) {
                    promises.push(
                        assignBoardRoles({
                            boardId: update.boardId,
                            userId: user.userId,
                            boardRoles: [] as any,
                        }),
                    );
                } else if (update.added.length > 0) {
                    promises.push(
                        assignBoardRoles({
                            boardId: update.boardId,
                            userId: user.userId,
                            boardRoles: ['TOTAL'] as any,
                        }),
                    );
                }

                // 상위 카테고리 변경 시 하위(자손)까지 확장
                const expandedAdded = new Set<number>(update.added);
                for (const categoryId of update.added) {
                    (await getSubCategoryIds(categoryId)).forEach((id) => expandedAdded.add(id));
                }
                const expandedRemoved = new Set<number>(update.removed);
                for (const categoryId of update.removed) {
                    (await getSubCategoryIds(categoryId)).forEach((id) => expandedRemoved.add(id));
                }

                expandedAdded.forEach((categoryId) =>
                    promises.push(addCategoryPermission(user.userId, categoryId)),
                );
                expandedRemoved.forEach((categoryId) =>
                    promises.push(removeCategoryPermission(user.userId, categoryId)),
                );
            }

            await Promise.all(promises);
            message.success(`${user.name} 님의 권한이 업데이트되었습니다.`);
            onSaved();
            onClose();
        } catch (error) {
            console.error('권한 업데이트 실패:', error);
            message.error('권한 업데이트 중 오류가 발생했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const isAllChecked = categories.length > 0 && checkedList.length === categories.length;
    const isIndeterminate = checkedList.length > 0 && checkedList.length < categories.length;
    const isBoardLoading = loadingBoardId === selectedBoardId && selectedBoardId != null;

    // 게시판 선택 옵션 — 편집으로 변경된 게시판은 표시
    const boardOptions = permBoards.map((b) => {
        const edited = editedByBoard[b.boardId];
        const label =
            edited !== undefined ? `${b.label} (${edited.length})` : b.label;
        return { value: b.boardId, label };
    });

    return (
        <Modal
            title={`게시판-카테고리 권한 부여 - ${user?.name ?? ''} (${user?.username ?? ''})`}
            open={open}
            onCancel={onClose}
            width={520}
            centered
            footer={[
                <Button key="cancel" onClick={onClose} disabled={isSaving}>
                    취소
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={changedBoardCount === 0}
                >
                    저장{changedBoardCount > 0 ? ` (${changedBoardCount}개 게시판)` : ''}
                </Button>,
            ]}
        >
            {permBoards.length === 0 ? (
                <Empty description="카테고리 별 권한을 사용하는 게시판이 없습니다." />
            ) : (
                <>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>게시판</div>
                    <Select
                        style={{ width: '100%' }}
                        value={selectedBoardId ?? undefined}
                        onChange={handleSelectBoard}
                        options={boardOptions}
                        placeholder="게시판을 선택하세요"
                    />

                    <Divider style={{ margin: '16px 0 12px' }} />

                    {isBoardLoading ? (
                        <div style={{ textAlign: 'center', padding: 24 }}>
                            <Spin />
                        </div>
                    ) : categories.length === 0 ? (
                        <p style={{ color: '#999' }}>카테고리가 존재하지 않습니다.</p>
                    ) : (
                        <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 4 }}>
                            <Checkbox
                                indeterminate={isIndeterminate}
                                onChange={handleCheckAll}
                                checked={isAllChecked}
                            >
                                <strong>전체 선택</strong>
                                {isAllChecked && (
                                    <Tag color="blue" style={{ marginLeft: 8 }}>
                                        전체 권한
                                    </Tag>
                                )}
                            </Checkbox>
                            <Divider style={{ margin: '12px 0' }} />
                            <Checkbox.Group
                                style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}
                                value={checkedList}
                                onChange={handleChange as any}
                            >
                                {categories.map((category) => (
                                    <Checkbox key={category.id} value={category.id}>
                                        {category.name}
                                    </Checkbox>
                                ))}
                            </Checkbox.Group>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default UserBoardPermissionModal;
